<template>
  <div v-if="user" class="mb-8">
    <div class="bg-gray-800 rounded-lg p-6 shadow-lg">
      <div class="flex flex-col md:flex-row md:items-center">
        <div class="flex-shrink-0 mb-4 md:mb-0 md:mr-6">
          <div
            class="bg-gray-700 rounded-full w-24 h-24 flex items-center justify-center text-4xl font-bold text-blue-400 shadow-md relative"
          >
            {{ user?.username?.charAt(0).toUpperCase() || '?' }}
            <!-- "Vi" bedž za trenutnog korisnika -->
            <div
              v-if="isCurrentUserProfile"
              class="absolute -top-2 -right-2 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full"
            >
              Vi
            </div>
          </div>
        </div>
        <div class="flex-grow">
          <div class="flex items-center mb-2">
            <h1 class="text-3xl font-bold text-blue-400">
              {{ user?.username || 'Korisnik' }}
            </h1>
            <!-- "Vaš profil" indikator za trenutnog korisnika -->
            <span
              v-if="isCurrentUserProfile"
              class="ml-3 bg-blue-900 text-blue-200 px-2 py-1 rounded text-sm"
            >
              Vaš profil
            </span>
          </div>
          <div class="flex flex-wrap items-center gap-4 mb-4">
            <div class="flex items-center text-gray-300">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 mr-2 text-green-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                  clip-rule="evenodd"
                />
              </svg>
              {{ user.city }}
            </div>
            <div class="flex items-center">
              <span class="text-green-400 font-medium mr-1"
                >+{{ user.positiveRatings }}</span
              >
              <span class="text-gray-400 mr-4">pozitivnih</span>
              <span class="text-red-400 font-medium mr-1"
                >-{{ user.negativeRatings }}</span
              >
              <span class="text-gray-400">negativnih</span>
            </div>
            <div class="flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 mr-2 text-blue-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"
                />
              </svg>
              <span class="text-blue-400 font-medium">{{
                followersCount
              }}</span>
              <span class="text-gray-400 ml-1">pratilaca</span>
            </div>
          </div>
          <!-- Akciona dugmad -->
          <div class="mt-2 flex gap-2">
            <!-- Follow dugme (samo za profile drugih korisnika) -->
            <button
              v-if="!isCurrentUserProfile"
              @click="handleToggleFollow"
              class="flex items-center bg-gray-700 hover:bg-gray-600 text-white py-2 px-4 rounded-md font-medium transition-colors border border-gray-600 hover:border-blue-400 shadow-md"
              :disabled="isLoading"
            >
              <!-- Loading spinner -->
              <div
                v-if="isLoading"
                class="animate-spin h-4 w-4 mr-2 border-t-2 border-b-2 border-blue-400 rounded-full"
              ></div>
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 mr-2"
                :class="{
                  'text-blue-500': following,
                  'text-gray-400': !following,
                }"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z"
                />
              </svg>
              <span v-if="following">Pratite</span>
              <span v-else>Zaprati</span>
            </button>

            <!-- Edit profile dugme (samo za sopstveni profil) -->
            <button
              v-if="isCurrentUserProfile"
              @click="$router.push('/account')"
              class="flex items-center bg-blue-600 hover:bg-blue-500 text-white py-2 px-4 rounded-md font-medium transition-colors shadow-md"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"
                />
              </svg>
              <span>Pogledaj profil</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, computed } from 'vue';
import { useStore } from 'vuex';

interface User {
  id: string;
  username: string;
  city: string;
  positiveRatings: number;
  negativeRatings: number;
  followersCount: number;
}

export default defineComponent({
  name: 'ProfileHeader',
  props: {
    user: {
      type: Object as PropType<User>,
      required: true,
    },
    following: {
      type: Boolean,
      required: true,
    },
    followersCount: {
      type: Number,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['toggle-follow'],
  setup(props, { emit }) {
    const store = useStore();

    // Proveriti da li je profil trenutnog korisnika koristeći Vuex store
    const isCurrentUserProfile = computed(() => {
      const currentUser = store.getters['auth/currentUser'];
      return currentUser && currentUser.id === props.user.id;
    });

    const handleToggleFollow = () => {
      emit('toggle-follow');
    };

    // Prvo učitati trenutnog korisnika ako nije već učitan
    store.dispatch('auth/ensureCurrentUser');

    return {
      handleToggleFollow,
      isCurrentUserProfile,
    };
  },
});
</script>
